import React from 'react';
import './NoteMessagePreview.css';

// チャットのノート活動バブル内に「どのノートか」を示すプレビューを表示する共有コンポーネント。
// 対象: 全チャット（1:1 / グループ / イベント）・全プラットフォームのWeb実装。
//
// 高さの考え方: タイトル・本文はメッセージ本体に同梱されて届くため初回描画時点で確定する。
// 画像だけが非同期ロードになるが、サムネイルは「固定サイズの正方形スロット」に描画するので
// 画像の実寸に依存せず高さが先に確定し、読み込みでレイアウトが動かない。

const THUMB_LIMIT = 3;

type NoteImage = {
  proxy_url?: string;
  proxyUrl?: string;
  url?: string;
  downloadUrl?: string;
};
type NoteMessage = {
  message_format?: string;
  note_title?: string;
  note_excerpt?: string;
  note_images?: NoteImage[];
  note_image_count?: number;
};

const normalizeFormat = (value: unknown) => String(value || '').trim().toLowerCase();

const pickThumbSrc = (image: NoteImage) => image?.proxy_url || image?.proxyUrl || image?.url || image?.downloadUrl || '';
const pickThumbFallback = (image: NoteImage) => image?.url || image?.downloadUrl || image?.proxy_url || image?.proxyUrl || '';

const NoteThumb = ({ image }: { image: NoteImage }) => {
  const primary = pickThumbSrc(image);
  const fallback = pickThumbFallback(image);
  const [failed, setFailed] = React.useState(false);
  const resolved = failed ? fallback : primary;
  if (!resolved) {
    return <span className="note-msg-preview__thumb note-msg-preview__thumb--empty" aria-hidden="true" />;
  }
  return (
    <img
      className="note-msg-preview__thumb"
      src={resolved}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => {
        if (!failed && fallback && fallback !== primary) setFailed(true);
      }}
    />
  );
};

const NoteMessagePreview = ({ message }: { message: NoteMessage }) => {
  const format = normalizeFormat(message?.message_format);

  // 削除されたノートは中身を出さず、灰色の「削除されたノート」を表示する。
  if (format === 'note_delete') {
    return <div className="note-msg-preview note-msg-preview--deleted">削除されたノート</div>;
  }

  const title = String(message?.note_title || '').trim();
  const excerpt = String(message?.note_excerpt || '').trim();
  const images = Array.isArray(message?.note_images) ? message.note_images : [];
  const totalCount = Number(message?.note_image_count) || images.length || 0;
  const shownImages = images.slice(0, THUMB_LIMIT);
  const overflow = Math.max(0, totalCount - shownImages.length);

  if (!title && !excerpt && shownImages.length === 0) return null;

  return (
    <div className="note-msg-preview">
      {title ? <div className="note-msg-preview__title">{title}</div> : null}
      {excerpt ? <div className="note-msg-preview__body">{excerpt}</div> : null}
      {shownImages.length ? (
        <div className="note-msg-preview__thumbs">
          {shownImages.map((image, index) => {
            const isLast = index === shownImages.length - 1;
            return (
              <div className="note-msg-preview__thumb-slot" key={index}>
                <NoteThumb image={image} />
                {overflow > 0 && isLast ? (
                  <span className="note-msg-preview__thumb-more">+{overflow}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default NoteMessagePreview;
